import { HTMLProps } from 'react';

import { Link } from 'react-router';

import { IconMapPin } from '@tabler/icons-react';
import { useAtom, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import {
  Divider,
  NavLink,
  SideNavigation,
  SideNavigationContent,
  SideNavigationWrapper,
  Typography,
} from '@cfa/react-components';

import { useAuth } from '@/hooks/useAuth';

import { cn } from '@/utils/tailwind';

import Constants from '@/constants';

import { TabProps } from '@/types/types';

import NavCountryLabel from './NavCountryLabel';

import { clearDocumentAtom } from '@/atoms/document';
import { licenseeLocationAtom } from '@/atoms/licensee-location';
import { PathwayLogo } from '@/icons';

interface NavbarDesktopProps {
  tabs: TabProps[];
}

const NavbarDesktop: React.FC<
  NavbarDesktopProps & HTMLProps<HTMLDivElement>
> = ({ tabs, className }) => {
  const { data } = useAuth();
  const isLicensee = data?.isLicensee;
  const licenseeLocations = data?.licenseeLocations ?? [];

  const { t } = useTranslation();
  const clearDocument = useSetAtom(clearDocumentAtom);
  const [{ selected: selectedLicenseeLocation }, sendLicenseeLocationAction] =
    useAtom(licenseeLocationAtom);

  const showLicenseeLocation =
    isLicensee &&
    !!selectedLicenseeLocation?.number &&
    licenseeLocations.length > 1;
  const preservedSubcategoryId = localStorage.getItem('preservedSubcategoryId');

  const onNavLinkClick = (tab: TabProps) => {
    const isExploreNav =
      tab.label === Constants.NAVBAR_NAMES.EXPLORE_NAVBAR_NAME;

    /**
     * We want to make sure we clear this value otherwise
     * the accordion will remain open when navigating away
     * from Explore and then back to Explore
     */
    if (preservedSubcategoryId || isExploreNav) {
      localStorage.setItem('preservedSubcategoryId', '');
    }

    // Clear document
    clearDocument();
  };

  const handleLicenseeLocationClick = () => {
    sendLicenseeLocationAction('show');
  };

  return (
    /** Although we are not using page content here, we need wrapper for collapsible behavior. */
    <SideNavigationWrapper className={cn('fixed z-10 bg-white', className)}>
      <SideNavigation className="hidden flex-col lg:flex lg:w-(--sidenav-width)!">
        <SideNavigationContent
          orientation="horizontal"
          variant="middle"
          className="mb-[22px] flex items-center justify-center"
          style={{
            marginTop: 'var(--web-alert-banner-height, 0px)',
          }}
        >
          <Link
            className="flex flex-col items-center justify-center gap-2"
            data-testid="PathwayLogo"
            title={t('Generic.appName')}
            to={`/`}
          >
            <PathwayLogo className="text-brand-navy! dark:text-contrast! h-10 w-9 lg:h-[72px] lg:w-16" />
            <NavCountryLabel />
          </Link>
        </SideNavigationContent>
        {showLicenseeLocation && (
          <>
            <button
              className="dark:hover:bg-base-2 flex justify-start gap-1.5 text-left hover:cursor-pointer hover:bg-[rgba(0,79,113,0.05)]"
              onClick={handleLicenseeLocationClick}
              type="button"
            >
              <div className="size-6">
                <IconMapPin className="text-brand-navy dark:text-contrast mx-0 my-1 size-6" />
              </div>
              <div>
                <Typography
                  className="leading-5"
                  color="secondary"
                  fontWeight="bold"
                  variant="body2"
                >
                  {selectedLicenseeLocation.name}
                </Typography>
                {/* Could not mapping to old primaryPalleteTeal. */}
                <Typography
                  className="leading-5 text-[rgb(62,177,200)]!"
                  fontWeight="medium"
                  variant="body2"
                >
                  {selectedLicenseeLocation.number}
                </Typography>
              </div>
            </button>
            <Divider
              className="my-4!"
              orientation="horizontal"
              variant="middle"
            />
          </>
        )}

        <SideNavigationContent className="flex flex-col" section="middle">
          {tabs.map((tab, index) => (
            <NavLink
              aria-label={`Navigate to ${tab.label}`}
              as={Link}
              data-testid={`Nav${tab.label}`}
              icon={tab.icon}
              isActive={tab.isActive}
              key={index}
              onClick={() => onNavLinkClick(tab)}
              to={tab.to}
              // TODO: this is a temporary fix for the configuración word being too long
              className={
                tab.label === 'Configuración' ? 'tracking-[-0.03em]' : ''
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </SideNavigationContent>
      </SideNavigation>
    </SideNavigationWrapper>
  );
};

export default NavbarDesktop;
